import { FC, useState } from 'react';
import { useContextSelector as useCS } from 'use-context-selector';
import { Modal } from '@mui/material';
import { CloseIcon, DeleteAccountModalIcon } from 'assets';
import { TUpdate } from 'domain/types';
import { Button, Input } from 'view/components';
import * as CS from 'view/modals/styled';
import {
  GlobalConfigContext,
  TGlobalConfigContextState,
} from 'domain/globalConfig/GlobalConfigContext';
import { globalConfigSelectors } from 'domain/globalConfig/selectors';
import { FormContainer, useFormContext } from 'react-hook-form-mui';
import { useUpdateApplicationStatus } from 'view/pages/candidates/hooks/useUpdateApplicationStatus';

interface IFormInput {
  feedback: string;
}

const JobSeekerApplyForJobPostModalFields = ({ setAddFeedback, onClose, isLoading }) => {
  const { watch } = useFormContext<IFormInput>();
  return (
    <>
      <Input
        name="feedback"
        label="Comment *"
        placeholder="e.g., “We’re looking for someone with more direct experience in product design.”"
        multiline
        rows={6}
        marginbottom={32}
      />

      <CS.ModalMainFooter>
        <Button
          variant="outlined"
          text="Cancel"
          onClick={() => {
            return setAddFeedback(false);
          }}
        />
        <Button
          variant="contained"
          text="Send Feedback"
          type="submit"
          disabled={!watch('feedback') || isLoading}
        />
      </CS.ModalMainFooter>
    </>
  );
};

const RecruiterDenyCandidateModal: FC<{
  hideCloseButton?: boolean;
  successFunction?: any;
  applicantId: any;
}> = ({ hideCloseButton = false, successFunction = () => {}, applicantId }) => {
  const [addFeedback, setAddFeedback] = useState(false);
  const isOpenRecruiterDenyCandidateModal: boolean = useCS(
    GlobalConfigContext,
    globalConfigSelectors.isOpenRecruiterDenyCandidateModal
  );

  const update: TUpdate<TGlobalConfigContextState> | undefined = useCS(
    GlobalConfigContext,
    globalConfigSelectors.update
  );

  const { isLoading: isUpdateApplicationStatusLoading, updateApplicationStatus } =
    useUpdateApplicationStatus();

  const onClose = () => {
    setAddFeedback(false);
    if (update) update({ isOpenRecruiterDenyCandidateModal: false });
  };

  const onSubmit = (formData: IFormInput) => {
    const dataToSend = {
      statusForCandidate: 'No Longer In Consideration',
      statusForRecruiter: 'Denied',
      feedback: formData.feedback,
    };
    updateApplicationStatus(dataToSend, applicantId, () => {
      successFunction();
      onClose();
    });
  };

  const onSkipForNowClick = () => {
    const dataToSend = {
      statusForCandidate: 'No Longer In Consideration',
      statusForRecruiter: 'Denied',
    };
    updateApplicationStatus(dataToSend, applicantId, () => {
      successFunction();
      onClose();
    });
  };

  const defaultValues = {
    feedback: '',
  };

  return (
    <Modal
      open={isOpenRecruiterDenyCandidateModal}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      {!addFeedback ? (
        <CS.ModalMainWrapper>
          {!hideCloseButton ? (
            <CS.CloseIconBox onClick={onClose}>
              <CloseIcon />
            </CS.CloseIconBox>
          ) : null}
          <CS.ModalMainBox>
            <CS.ModalMainImage>
              <DeleteAccountModalIcon />
            </CS.ModalMainImage>
            <CS.ModalMainHeader>
              <CS.ModalMainHeaderTitle>
                Share why candidate isn’t the right fit
              </CS.ModalMainHeaderTitle>
              <CS.ModalMainHeaderSubTitle>
                {
                  'Providing a brief note can help them improve or understand your decision. \n (This feedback will be shared with the candidate.)'
                }
              </CS.ModalMainHeaderSubTitle>
            </CS.ModalMainHeader>
          </CS.ModalMainBox>
          <CS.ModalMainFooter>
            <Button
              variant="outlined"
              text="Skip for Now"
              onClick={onSkipForNowClick}
              disabled={isUpdateApplicationStatusLoading}
            />
            <Button
              variant="contained"
              text="Add Feedback"
              onClick={() => {
                setAddFeedback(true);
              }}
            />
          </CS.ModalMainFooter>
        </CS.ModalMainWrapper>
      ) : (
        <CS.ModalMainWrapper>
          {!hideCloseButton ? (
            <CS.CloseIconBox onClick={onClose}>
              <CloseIcon />
            </CS.CloseIconBox>
          ) : null}
          <CS.ModalMainBox>
            <CS.ModalMainHeader>
              <CS.ModalMainHeaderTitle>Add Feedback</CS.ModalMainHeaderTitle>
              <CS.ModalMainHeaderSubTitle>
                {
                  'Providing a brief note can help them improve or understand your decision. \n (This message will be shared with the candidate.)'
                }
              </CS.ModalMainHeaderSubTitle>
            </CS.ModalMainHeader>
          </CS.ModalMainBox>
          <FormContainer
            key={JSON.stringify(defaultValues)}
            defaultValues={defaultValues}
            onSuccess={onSubmit}
          >
            <JobSeekerApplyForJobPostModalFields
              setAddFeedback={setAddFeedback}
              onClose={onClose}
              isLoading={isUpdateApplicationStatusLoading}
            />
          </FormContainer>
        </CS.ModalMainWrapper>
      )}
    </Modal>
  );
};

export default RecruiterDenyCandidateModal;
